import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ExternalLink, Loader2 } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { META_INTEGRATION_ENABLED } from '@/config/env'
import type { CrossCriteria, CrossSettings } from '@/services/betterplaceService'
import { whatsappService, type WABA, type WhatsAppTemplate } from '@/services/whatsappService'
import { resolvePreferredWhatsAppWabaId } from '@/services/whatsappWabaUtils'
import {
  analyzeBetterplaceTemplate,
  buildBetterplaceTemplateOptions,
  findBetterplaceTemplate,
  getBetterplaceTemplateFieldNames,
  getBetterplaceVariableSpecs,
  getEffectiveBetterplaceVariableKeys,
  type BetterplaceTemplateRole,
} from '@/components/actions/betterplaceTemplateUtils'

export type BetterplaceDraft = {
  enabled: boolean
  interval_hours: number
  max_listings: number
}

export type BetterplaceSync = {
  enabled: boolean
  interval_minutes: number
  max_listings: number
}

export interface BetterplaceCrucesFormProps {
  betterplaceDraft: BetterplaceDraft
  setBetterplaceDraft: (draft: BetterplaceDraft | ((prev: BetterplaceDraft) => BetterplaceDraft)) => void
  betterplaceLoading: boolean
  betterplaceSaving: boolean
  betterplaceError: string | null
  hasBetterplaceChanges: boolean
  onSaveBetterplace: () => void
  onCancelBetterplace: () => void
  crossCriteriaDraft: CrossCriteria
  setCrossCriteriaDraft: (draft: CrossCriteria | ((prev: CrossCriteria) => CrossCriteria)) => void
  crossCriteriaLoading: boolean
  crossCriteriaSaving: boolean
  crossCriteriaError: string | null
  hasCrossCriteriaChanges: boolean
  onSaveCrossCriteria: () => void
  onCancelCrossCriteria: () => void
  crossSettingsDraft: CrossSettings
  setCrossSettingsDraft: (draft: CrossSettings | ((prev: CrossSettings) => CrossSettings)) => void
  crossSettingsLoading: boolean
  crossSettingsSaving: boolean
  crossSettingsError: string | null
  hasCrossSettingsChanges: boolean
  onSaveCrossSettings: () => void
  onCancelCrossSettings: () => void
  connectedWabaId?: string | null
  whatsappConnected?: boolean
  onWhatsappTemplatesChange?: (templates: WhatsAppTemplate[]) => void
  lang?: string
  whatsappDispatchEnabled: boolean
  whatsappDispatchLoading: boolean
  whatsappDispatchSaving: boolean
  whatsappDispatchError?: string | null
  onToggleWhatsappDispatch: (enabled: boolean) => void
}

type NumericSelectOption = {
  value: number
  label: string
}

const BETTERPLACE_INTERVAL_HOUR_OPTIONS = [
  { value: 1, label: '1 hora' },
  { value: 6, label: '6 horas' },
  { value: 12, label: '12 horas' },
  { value: 24, label: '24 horas' },
] as const

const BETTERPLACE_MAX_LISTINGS_OPTIONS = [
  { value: 5, label: '5 casas' },
  { value: 10, label: '10 casas' },
  { value: 20, label: '20 casas' },
  { value: 50, label: '50 casas' },
  { value: 100, label: '100 casas' },
] as const

const EMPTY_TEMPLATE_VALUE = '__none__'
const EMPTY_NUMERIC_SELECT_VALUE = '__empty__'

const BETTERPLACE_ROOMS_VARIANCE_OPTIONS: NumericSelectOption[] = [
  { value: 0, label: '0 habitaciones' },
  { value: 1, label: '1 habitación' },
  { value: 2, label: '2 habitaciones' },
  { value: 3, label: '3 habitaciones' },
]

const BETTERPLACE_BATHROOMS_VARIANCE_OPTIONS: NumericSelectOption[] = [
  { value: 0, label: '0 baños' },
  { value: 1, label: '1 baño' },
  { value: 2, label: '2 baños' },
  { value: 3, label: '3 baños' },
]

const BETTERPLACE_PRICE_VARIANCE_OPTIONS: NumericSelectOption[] = [
  { value: 0, label: '0 %' },
  { value: 5, label: '5 %' },
  { value: 10, label: '10 %' },
  { value: 15, label: '15 %' },
  { value: 20, label: '20 %' },
  { value: 25, label: '25 %' },
  { value: 30, label: '30 %' },
]

const BETTERPLACE_DISTANCE_OPTIONS: NumericSelectOption[] = [
  { value: 1, label: '1 km' },
  { value: 3, label: '3 km' },
  { value: 5, label: '5 km' },
  { value: 10, label: '10 km' },
  { value: 20, label: '20 km' },
]

function renderNumericSelectItems(currentValue: number | undefined, options: NumericSelectOption[]) {
  return [
    <SelectItem key={EMPTY_NUMERIC_SELECT_VALUE} value={EMPTY_NUMERIC_SELECT_VALUE}>—</SelectItem>,
    ...(!options.some((option) => option.value === currentValue) && currentValue !== undefined
      ? [
          <SelectItem key={`legacy-${currentValue}`} value={String(currentValue)}>
            {`${currentValue} (legacy)`}
          </SelectItem>,
        ]
      : []),
    ...options.map((option) => (
      <SelectItem key={option.value} value={String(option.value)}>
        {option.label}
      </SelectItem>
    )),
  ]
}

type BetterplaceTemplateConfiguratorProps = {
  role: BetterplaceTemplateRole
  title: string
  helperText: string
  recipientNote?: string
  recipientPhone?: {
    value: string
    onChange: (value: string | undefined) => void
    disabled?: boolean
    label?: string
    placeholder?: string
    helperText?: string
  }
  crossSettingsDraft: CrossSettings
  setCrossSettingsDraft: (draft: CrossSettings | ((prev: CrossSettings) => CrossSettings)) => void
  crossSettingsLoading: boolean
  crossSettingsSaving: boolean
  whatsappTemplatesLoading: boolean
  whatsappTemplates: WhatsAppTemplate[]
  selectedWabaId: string
  wabasAvailable: boolean
}

function BetterplaceTemplateConfigurator({
  role,
  title,
  helperText,
  recipientNote,
  recipientPhone,
  crossSettingsDraft,
  setCrossSettingsDraft,
  crossSettingsLoading,
  crossSettingsSaving,
  whatsappTemplatesLoading,
  whatsappTemplates,
  selectedWabaId,
  wabasAvailable,
}: BetterplaceTemplateConfiguratorProps) {
  const { name, language, variables } = getBetterplaceTemplateFieldNames(role)
  const selectedName = typeof crossSettingsDraft[name] === 'string' ? crossSettingsDraft[name] : undefined
  const selectedLanguage = typeof crossSettingsDraft[language] === 'string' ? crossSettingsDraft[language] : undefined
  const selectedTemplate = findBetterplaceTemplate(whatsappTemplates, selectedName, selectedLanguage)
  const analysis = analyzeBetterplaceTemplate(selectedTemplate)
  const variableSpecs = getBetterplaceVariableSpecs(role)
  const effectiveVariableKeys = getEffectiveBetterplaceVariableKeys(crossSettingsDraft, role, selectedTemplate)
  const options = buildBetterplaceTemplateOptions(whatsappTemplates)
  const selectedTemplateKey = selectedTemplate
    ? `${selectedTemplate.name}:${selectedTemplate.language}`
    : EMPTY_TEMPLATE_VALUE
  const hasSelectableTemplates = options.length > 0

  const handleTemplateChange = (value: string) => {
    if (value === EMPTY_TEMPLATE_VALUE) {
      setCrossSettingsDraft((prev) => ({
        ...prev,
        [name]: undefined,
        [language]: undefined,
        [variables]: undefined,
      }))
      return
    }

    const separatorIndex = value.indexOf(':')
    const templateName = separatorIndex >= 0 ? value.slice(0, separatorIndex) : value
    const templateLanguage = separatorIndex >= 0 ? value.slice(separatorIndex + 1) : undefined
    const template = findBetterplaceTemplate(whatsappTemplates, templateName, templateLanguage)
    setCrossSettingsDraft((prev) => {
      const nextSettings: CrossSettings = {
        ...prev,
        [name]: templateName,
        [language]: template?.language || templateLanguage || undefined,
      }
      // Misma correlacion que TypeScript no puede seguir que en
      // betterplaceTemplateUtils.ts: indexar con una union de claves resuelve el
      // valor como la interseccion de todos los posibles. El valor lo sigue
      // comprobando getEffectiveBetterplaceVariableKeys.
      ;(nextSettings as Record<string, unknown>)[variables] =
        getEffectiveBetterplaceVariableKeys(nextSettings, role, template)
      return nextSettings
    })
  }

  const handleVariableChange = (index: number, nextKey: string) => {
    setCrossSettingsDraft((prev) => {
      const template = findBetterplaceTemplate(
        whatsappTemplates,
        typeof prev[name] === 'string' ? prev[name] : undefined,
        typeof prev[language] === 'string' ? prev[language] : undefined,
      )
      const nextVariables = [...getEffectiveBetterplaceVariableKeys(prev, role, template)]
      nextVariables[index] = nextKey as typeof nextVariables[number]
      return {
        ...prev,
        [variables]: nextVariables,
      }
    })
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <div className="text-sm font-medium text-foreground">{title}</div>
        <p className="text-xs text-muted-foreground">{helperText}</p>
        {recipientNote && (
          <p className="text-xs text-muted-foreground">{recipientNote}</p>
        )}
      </div>

      {recipientPhone && (
        <label className="grid gap-2 text-sm">
          <span className="text-muted-foreground">
            {recipientPhone.label ?? 'Número de destino'}
          </span>
          <Input
            value={recipientPhone.value}
            onChange={(event) =>
              recipientPhone.onChange(event.target.value.trim() || undefined)
            }
            disabled={
              recipientPhone.disabled ||
              crossSettingsLoading ||
              crossSettingsSaving
            }
            placeholder={recipientPhone.placeholder ?? '+34 600 000 000'}
          />
          {recipientPhone.helperText && (
            <p className="text-xs text-muted-foreground">{recipientPhone.helperText}</p>
          )}
        </label>
      )}

      <div className="space-y-2">
        <Label>Plantilla aprobada de Meta</Label>
        <Select
          value={selectedTemplateKey}
          onValueChange={handleTemplateChange}
          disabled={
            crossSettingsLoading ||
            crossSettingsSaving ||
            whatsappTemplatesLoading ||
            !selectedWabaId ||
            !wabasAvailable ||
            !hasSelectableTemplates
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar plantilla" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={EMPTY_TEMPLATE_VALUE}>—</SelectItem>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {analysis.bodyText ? (
          <p className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground whitespace-pre-wrap">
            {analysis.bodyText}
          </p>
        ) : null}
      </div>

      {!analysis.compatible && analysis.reason && selectedTemplate && (
        <Alert variant="destructive">
          <AlertDescription>{analysis.reason}</AlertDescription>
        </Alert>
      )}

      {analysis.compatible && analysis.bodyVariableCount > 0 && (
        <div className="space-y-2">
          <Label>Mapa de variables</Label>
          <p className="text-xs text-muted-foreground">
            Asigna cada hueco de la plantilla a un dato del cruce Betterplace.
          </p>
          {effectiveVariableKeys.map((variableKey, index) => (
            <div key={`${role}-${index}`} className="grid grid-cols-[80px_1fr] items-center gap-2">
              <span className="text-sm font-medium">{`{{${index + 1}}}`}</span>
              <Select
                value={variableKey}
                onValueChange={(nextValue) => handleVariableChange(index, nextValue)}
                disabled={crossSettingsLoading || crossSettingsSaving}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar dato de Betterplace" />
                </SelectTrigger>
                <SelectContent>
                  {variableSpecs.map((spec) => (
                    <SelectItem key={`${role}-${spec.key}`} value={spec.key}>
                      {spec.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}

      {selectedTemplate && analysis.compatible && analysis.bodyVariableCount === 0 && (
        <p className="text-xs text-muted-foreground">
          Esta plantilla no tiene variables en el cuerpo. Se enviará tal cual en el cruce.
        </p>
      )}
    </div>
  )
}

export function BetterplaceCrucesForm({
  betterplaceDraft,
  setBetterplaceDraft,
  betterplaceLoading,
  betterplaceSaving,
  betterplaceError,
  hasBetterplaceChanges,
  onSaveBetterplace,
  onCancelBetterplace,
  crossCriteriaDraft,
  setCrossCriteriaDraft,
  crossCriteriaLoading,
  crossCriteriaSaving,
  crossCriteriaError,
  hasCrossCriteriaChanges,
  onSaveCrossCriteria,
  onCancelCrossCriteria,
  crossSettingsDraft,
  setCrossSettingsDraft,
  crossSettingsLoading,
  crossSettingsSaving,
  crossSettingsError,
  hasCrossSettingsChanges,
  onSaveCrossSettings,
  onCancelCrossSettings,
  connectedWabaId,
  whatsappConnected = false,
  onWhatsappTemplatesChange,
  lang,
  whatsappDispatchEnabled,
  whatsappDispatchLoading,
  whatsappDispatchSaving,
  whatsappDispatchError,
  onToggleWhatsappDispatch,
}: BetterplaceCrucesFormProps) {
  const { t } = useTranslation()
  const settingsLang = lang || 'es'
  const [wabas, setWabas] = useState<WABA[]>([])
  const [selectedWabaId, setSelectedWabaId] = useState('')
  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsAppTemplate[]>([])
  const [whatsappTemplatesLoading, setWhatsappTemplatesLoading] = useState(false)
  const [whatsappTemplatesError, setWhatsappTemplatesError] = useState<string | null>(null)

  useEffect(() => {
    if (!META_INTEGRATION_ENABLED || !whatsappConnected) {
      setWabas([])
      setSelectedWabaId('')
      setWhatsappTemplates([])
      onWhatsappTemplatesChange?.([])
      return
    }

    let cancelled = false

    const loadWabas = async () => {
      setWhatsappTemplatesLoading(true)
      setWhatsappTemplatesError(null)
      try {
        const availableWabas = await whatsappService.getWabas()
        if (cancelled) return
        setWabas(availableWabas)
        const preferredWabaId = resolvePreferredWhatsAppWabaId(availableWabas, connectedWabaId)
        setSelectedWabaId(preferredWabaId)
        if (!preferredWabaId) {
          setWhatsappTemplates([])
          onWhatsappTemplatesChange?.([])
          return
        }
        const templates = await whatsappService.getTemplates(preferredWabaId)
        if (cancelled) return
        setWhatsappTemplates(templates)
        onWhatsappTemplatesChange?.(templates)
      } catch (error) {
        if (cancelled) return
        setWhatsappTemplates([])
        onWhatsappTemplatesChange?.([])
        setWhatsappTemplatesError(
          error instanceof Error ? error.message : 'No se pudieron cargar las plantillas de Meta.',
        )
      } finally {
        if (!cancelled) {
          setWhatsappTemplatesLoading(false)
        }
      }
    }

    void loadWabas()

    return () => {
      cancelled = true
    }
  }, [connectedWabaId, onWhatsappTemplatesChange, whatsappConnected])

  const handleWabaChange = async (wabaId: string) => {
    setSelectedWabaId(wabaId)
    setWhatsappTemplatesLoading(true)
    setWhatsappTemplatesError(null)
    try {
      const templates = await whatsappService.getTemplates(wabaId)
      setWhatsappTemplates(templates)
      onWhatsappTemplatesChange?.(templates)
    } catch (error) {
      setWhatsappTemplates([])
      onWhatsappTemplatesChange?.([])
      setWhatsappTemplatesError(
        error instanceof Error ? error.message : 'No se pudieron cargar las plantillas de Meta.',
      )
    } finally {
      setWhatsappTemplatesLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <section className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">Automatización Betterplace</h4>
                <p className="text-sm text-muted-foreground">
                  Controla solo la sincronización de listados desde Betterplace. Los envíos de WhatsApp tienen su propio interruptor abajo.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={betterplaceDraft.enabled ? 'default' : 'secondary'}>
                  {betterplaceDraft.enabled ? 'Activa' : 'Pausada'}
                </Badge>
                <Switch
                  checked={betterplaceDraft.enabled}
                  onCheckedChange={(checked) => setBetterplaceDraft({ ...betterplaceDraft, enabled: checked })}
                  disabled={betterplaceLoading || betterplaceSaving}
                  aria-label="Toggle Betterplace automation"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Intervalo de requests y casas por request.
                </p>
              </div>
              <span className="text-sm text-muted-foreground">Se aplica a cruces futuros</span>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="grid gap-2 text-sm">
                <span className="text-muted-foreground">Intervalo de requests</span>
                <Select
                  value={String(betterplaceDraft.interval_hours ?? 24)}
                  onValueChange={(value) =>
                    setBetterplaceDraft({
                      ...betterplaceDraft,
                      interval_hours: Number(value),
                    })
                  }
                  disabled={betterplaceLoading || betterplaceSaving}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {!BETTERPLACE_INTERVAL_HOUR_OPTIONS.some(
                      (option) => option.value === betterplaceDraft.interval_hours,
                    ) && (
                      <SelectItem value={String(betterplaceDraft.interval_hours)}>
                        {`${betterplaceDraft.interval_hours} horas (legacy)`}
                      </SelectItem>
                    )}
                    {BETTERPLACE_INTERVAL_HOUR_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <label className="grid gap-2 text-sm">
                <span className="flex items-center justify-between text-muted-foreground">
                  <span>Casas por request</span>
                  <span className="text-xs">máx. 100</span>
                </span>
                <Select
                  value={String(betterplaceDraft.max_listings ?? 10)}
                  onValueChange={(value) =>
                    setBetterplaceDraft({
                      ...betterplaceDraft,
                      max_listings: Number(value),
                    })
                  }
                  disabled={betterplaceLoading || betterplaceSaving}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {!BETTERPLACE_MAX_LISTINGS_OPTIONS.some(
                      (option) => option.value === betterplaceDraft.max_listings,
                    ) && (
                      <SelectItem value={String(betterplaceDraft.max_listings)}>
                        {`${betterplaceDraft.max_listings} casas (legacy)`}
                      </SelectItem>
                    )}
                    {BETTERPLACE_MAX_LISTINGS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <div className="flex items-end gap-2 md:col-span-2">
                <Button
                  variant="outline"
                  onClick={onCancelBetterplace}
                  disabled={!hasBetterplaceChanges || betterplaceSaving}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={onSaveBetterplace}
                  disabled={!hasBetterplaceChanges || betterplaceSaving}
                >
                  {betterplaceSaving ? 'Guardando...' : t('common.save')}
                </Button>
              </div>
            </div>
            {betterplaceError && (
              <div className="text-sm text-destructive">{betterplaceError}</div>
            )}
      </section>

      {/* 2) Criterios de cruce */}
      <section className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold">Criterios de cruce</h4>
            <Badge variant="secondary" className="bg-warning/10 text-warning border-warning/30 text-xs">
              Solo afectará a cruces futuros
            </Badge>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <label className="grid gap-2 text-sm">
              <span className="text-muted-foreground">Varianza de habitaciones</span>
              <Select
                value={
                  crossCriteriaDraft.roomsVariance === undefined
                    ? EMPTY_NUMERIC_SELECT_VALUE
                    : String(crossCriteriaDraft.roomsVariance)
                }
                onValueChange={(value) =>
                  setCrossCriteriaDraft({
                    ...crossCriteriaDraft,
                    roomsVariance: value === EMPTY_NUMERIC_SELECT_VALUE ? undefined : Number(value),
                  })
                }
                disabled={crossCriteriaLoading || crossCriteriaSaving}
              >
                <SelectTrigger>
                  <SelectValue placeholder="1 habitación" />
                </SelectTrigger>
                <SelectContent>
                  {renderNumericSelectItems(crossCriteriaDraft.roomsVariance, BETTERPLACE_ROOMS_VARIANCE_OPTIONS)}
                </SelectContent>
              </Select>
            </label>
            <label className="grid gap-2 text-sm">
              <span className="text-muted-foreground">Varianza de baños</span>
              <Select
                value={
                  crossCriteriaDraft.bathroomsVariance === undefined
                    ? EMPTY_NUMERIC_SELECT_VALUE
                    : String(crossCriteriaDraft.bathroomsVariance)
                }
                onValueChange={(value) =>
                  setCrossCriteriaDraft({
                    ...crossCriteriaDraft,
                    bathroomsVariance: value === EMPTY_NUMERIC_SELECT_VALUE ? undefined : Number(value),
                  })
                }
                disabled={crossCriteriaLoading || crossCriteriaSaving}
              >
                <SelectTrigger>
                  <SelectValue placeholder="1 baño" />
                </SelectTrigger>
                <SelectContent>
                  {renderNumericSelectItems(crossCriteriaDraft.bathroomsVariance, BETTERPLACE_BATHROOMS_VARIANCE_OPTIONS)}
                </SelectContent>
              </Select>
            </label>
            <label className="grid gap-2 text-sm">
              <span className="text-muted-foreground">Varianza precio arriba (%)</span>
              <Select
                value={
                  crossCriteriaDraft.priceVarianceUp === undefined
                    ? EMPTY_NUMERIC_SELECT_VALUE
                    : String(crossCriteriaDraft.priceVarianceUp)
                }
                onValueChange={(value) =>
                  setCrossCriteriaDraft({
                    ...crossCriteriaDraft,
                    priceVarianceUp: value === EMPTY_NUMERIC_SELECT_VALUE ? undefined : Number(value),
                  })
                }
                disabled={crossCriteriaLoading || crossCriteriaSaving}
              >
                <SelectTrigger>
                  <SelectValue placeholder="15 %" />
                </SelectTrigger>
                <SelectContent>
                  {renderNumericSelectItems(crossCriteriaDraft.priceVarianceUp, BETTERPLACE_PRICE_VARIANCE_OPTIONS)}
                </SelectContent>
              </Select>
            </label>
            <label className="grid gap-2 text-sm">
              <span className="text-muted-foreground">Varianza precio abajo (%)</span>
              <Select
                value={
                  crossCriteriaDraft.priceVarianceDown === undefined
                    ? EMPTY_NUMERIC_SELECT_VALUE
                    : String(crossCriteriaDraft.priceVarianceDown)
                }
                onValueChange={(value) =>
                  setCrossCriteriaDraft({
                    ...crossCriteriaDraft,
                    priceVarianceDown: value === EMPTY_NUMERIC_SELECT_VALUE ? undefined : Number(value),
                  })
                }
                disabled={crossCriteriaLoading || crossCriteriaSaving}
              >
                <SelectTrigger>
                  <SelectValue placeholder="10 %" />
                </SelectTrigger>
                <SelectContent>
                  {renderNumericSelectItems(crossCriteriaDraft.priceVarianceDown, BETTERPLACE_PRICE_VARIANCE_OPTIONS)}
                </SelectContent>
              </Select>
            </label>
            <label className="grid gap-2 text-sm">
              <span className="text-muted-foreground">Distancia (km)</span>
              <Select
                value={
                  crossCriteriaDraft.distanceKm === undefined
                    ? EMPTY_NUMERIC_SELECT_VALUE
                    : String(crossCriteriaDraft.distanceKm)
                }
                onValueChange={(value) =>
                  setCrossCriteriaDraft({
                    ...crossCriteriaDraft,
                    distanceKm: value === EMPTY_NUMERIC_SELECT_VALUE ? undefined : Number(value),
                  })
                }
                disabled={crossCriteriaLoading || crossCriteriaSaving}
              >
                <SelectTrigger>
                  <SelectValue placeholder="5 km" />
                </SelectTrigger>
                <SelectContent>
                  {renderNumericSelectItems(crossCriteriaDraft.distanceKm, BETTERPLACE_DISTANCE_OPTIONS)}
                </SelectContent>
              </Select>
            </label>
            <label className="grid gap-2 text-sm">
              <span className="text-muted-foreground">Días para expirar el cruce</span>
              <div className="relative">
                <Input
                  type="number"
                  min={0}
                  value={crossCriteriaDraft.expiresInDays ?? ''}
                  onChange={(e) => setCrossCriteriaDraft({ ...crossCriteriaDraft, expiresInDays: e.target.value === '' ? undefined : Number(e.target.value) })}
                  disabled
                  placeholder="30"
                  className="pr-12"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  días
                </span>
              </div>
            </label>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={onCancelCrossCriteria}
              disabled={!hasCrossCriteriaChanges || crossCriteriaSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={onSaveCrossCriteria}
              disabled={!hasCrossCriteriaChanges || crossCriteriaSaving}
            >
              {crossCriteriaSaving ? 'Guardando...' : t('common.save')}
            </Button>
          </div>
          {crossCriteriaError && (
            <div className="text-sm text-destructive">{crossCriteriaError}</div>
          )}
      </section>

      <section className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h4 className="text-sm font-semibold">Envíos de WhatsApp</h4>
              <div className="flex items-center gap-2">
                <Badge variant={whatsappDispatchEnabled ? 'default' : 'secondary'}>
                  {whatsappDispatchEnabled ? 'Activos' : 'Pausados'}
                </Badge>
                <Switch
                  checked={whatsappDispatchEnabled}
                  onCheckedChange={(checked) => onToggleWhatsappDispatch(checked)}
                  disabled={whatsappDispatchLoading || whatsappDispatchSaving}
                  aria-label="Toggle envíos de WhatsApp de cruces"
                />
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Interruptor independiente del sync: mientras esté pausado no se envía ningún WhatsApp de cruces, aunque la sincronización siga activa.
            </p>
            {whatsappDispatchError && (
              <Alert variant="destructive">
                <AlertDescription>{whatsappDispatchError}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-1">
              <h4 className="text-sm font-semibold">Plantillas de WhatsApp</h4>
              <p className="text-sm text-muted-foreground">
                Elige la cuenta de WhatsApp, la plantilla aprobada en Meta y mapea cada variable con los datos del cruce Betterplace.
              </p>
              <p className="text-xs text-muted-foreground">
                Si falta una plantilla, créala en{' '}
                <Link
                  to={`/${settingsLang}/settings/meta`}
                  className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
                >
                  Configuración → Meta → WhatsApp Templates
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
                .
              </p>
            </div>

            {whatsappTemplatesLoading && wabas.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Cargando cuentas y plantillas de Meta...
              </div>
            ) : null}

            {whatsappTemplatesError && (
              <Alert variant="destructive">
                <AlertDescription>{whatsappTemplatesError}</AlertDescription>
              </Alert>
            )}

            {wabas.length > 0 && (
              <div className="space-y-2">
                <Label>Cuenta de WhatsApp</Label>
                <Select
                  value={selectedWabaId || undefined}
                  onValueChange={(value) => void handleWabaChange(value)}
                  disabled={crossSettingsLoading || crossSettingsSaving || whatsappTemplatesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar WABA" />
                  </SelectTrigger>
                  <SelectContent>
                    {wabas.map((waba) => (
                      <SelectItem key={waba.id} value={waba.id}>
                        {waba.name || waba.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {!whatsappTemplatesLoading && !whatsappTemplatesError && selectedWabaId && whatsappTemplates.length === 0 && (
              <Alert>
                <AlertDescription>
                  No hay plantillas aprobadas en esta cuenta de WhatsApp.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <BetterplaceTemplateConfigurator
                role="buyers"
                title="Plantilla contacto"
                helperText="Se envía al interesado cuando hay un cruce Betterplace compatible."
                recipientNote="Destinatario: teléfono del contacto en Betterplace (no hace falta configurarlo aquí)."
                crossSettingsDraft={crossSettingsDraft}
                setCrossSettingsDraft={setCrossSettingsDraft}
                crossSettingsLoading={crossSettingsLoading}
                crossSettingsSaving={crossSettingsSaving}
                whatsappTemplatesLoading={whatsappTemplatesLoading}
                whatsappTemplates={whatsappTemplates}
                selectedWabaId={selectedWabaId}
                wabasAvailable={wabas.length > 0}
              />

              <BetterplaceTemplateConfigurator
                role="coordination"
                title="Plantilla empleados"
                helperText="Se envía al equipo cuando el contacto confirma interés en la propiedad."
                recipientPhone={{
                  value: crossSettingsDraft.coordination_phone ?? '',
                  onChange: (value) =>
                    setCrossSettingsDraft((prev) => ({
                      ...prev,
                      coordination_phone: value,
                    })),
                  disabled: crossSettingsLoading || crossSettingsSaving,
                  label: 'Número de empleados (destino)',
                  placeholder: '+34 600 000 000',
                  helperText:
                    'WhatsApp fijo del equipo que recibirá la plantilla de empleados.',
                }}
                crossSettingsDraft={crossSettingsDraft}
                setCrossSettingsDraft={setCrossSettingsDraft}
                crossSettingsLoading={crossSettingsLoading}
                crossSettingsSaving={crossSettingsSaving}
                whatsappTemplatesLoading={whatsappTemplatesLoading}
                whatsappTemplates={whatsappTemplates}
                selectedWabaId={selectedWabaId}
                wabasAvailable={wabas.length > 0}
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={onCancelCrossSettings}
                disabled={!hasCrossSettingsChanges || crossSettingsSaving}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={onSaveCrossSettings}
                disabled={!hasCrossSettingsChanges || crossSettingsSaving}
              >
                {crossSettingsSaving ? 'Guardando...' : t('common.save')}
              </Button>
            </div>
            {crossSettingsError && (
              <div className="text-sm text-destructive">{crossSettingsError}</div>
            )}
      </section>
    </div>
  )
}
